// SPDX-License-Identifier: BUSL-1.1

/**
 * Gate hit rates governance card — shows policy gate evaluation breakdown
 * for the last 24 hours from the audit log.
 *
 */

import type * as React from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { apiFetch } from '@/lib/api-client';
import type { AuditResponse, AuditEntry } from '@/types/api';

interface GateBreakdown {
  safeAutoAllow: number;
  guardedAutoAllow: number;
  manualQueued: number;
  rejected: number;
}

/** Classify audit entries into gate evaluation categories. */
function classifyGates(entries: AuditEntry[]): GateBreakdown {
  const breakdown: GateBreakdown = {
    safeAutoAllow: 0,
    guardedAutoAllow: 0,
    manualQueued: 0,
    rejected: 0,
  };

  for (const entry of entries) {
    if (entry.action === 'policy.confirm' && entry.resourceType === 'safe') {
      breakdown.safeAutoAllow++;
    } else if (entry.action === 'policy.confirm') {
      breakdown.guardedAutoAllow++;
    } else if (entry.action === 'moderation.requested') {
      breakdown.manualQueued++;
    } else if (entry.action === 'policy.reject') {
      breakdown.rejected++;
    }
  }

  return breakdown;
}

interface GateRowProps {
  label: string;
  count: number;
  total: number;
  colorClass: string;
  barColorClass: string;
}

function GateRow({ label, count, total, colorClass, barColorClass }: GateRowProps) {
  const pct = total > 0 ? (count / total) * 100 : 0;
  return (
    <div className="space-y-1">
      <div className="flex items-center justify-between">
        <span className="text-xs text-neutral-400">{label}</span>
        <Badge variant="secondary" className={`font-mono text-[11px] ${colorClass}`}>
          {count}
        </Badge>
      </div>
      <div className="h-1.5 w-full overflow-hidden rounded-full bg-neutral-800">
        <div
          className={`h-full rounded-full transition-all ${barColorClass}`}
          style={{ width: `${Math.max(pct, pct > 0 ? 2 : 0)}%` }}
        />
      </div>
    </div>
  );
}

export function GovernanceGatesCard(): React.ReactElement {
  const fromIso = new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString();
  const { data, isLoading } = useQuery({
    queryKey: ['audit', 'gates-24h'],
    queryFn: () =>
      apiFetch<AuditResponse>(
        `/audit?action=policy.confirm,policy.reject,moderation.requested&from=${fromIso}`,
      ),
    refetchInterval: 60_000,
  });

  if (isLoading) {
    return (
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-sm">Gate Hit Rates</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} className="h-5 w-full" />
          ))}
        </CardContent>
      </Card>
    );
  }

  const entries = data?.entries ?? [];
  const breakdown = classifyGates(entries);
  const total = entries.length;

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="text-sm">Gate Hit Rates</CardTitle>
        <Link
          to="/audit"
          className="text-xs text-neutral-500 transition hover:text-neutral-300"
        >
          Full log →
        </Link>
      </CardHeader>

      <CardContent className="space-y-3">
        <div className="flex items-baseline gap-2">
          <p className="font-mono text-2xl font-semibold text-neutral-100">
            {total}
          </p>
          <p className="text-xs text-neutral-500">evaluations (24h)</p>
        </div>

        <div className="space-y-2.5 border-t border-neutral-800 pt-3">
          <GateRow
            label="Safe auto-allow"
            count={breakdown.safeAutoAllow}
            total={total}
            colorClass="text-green-400"
            barColorClass="bg-green-500"
          />
          <GateRow
            label="Guarded auto-allow"
            count={breakdown.guardedAutoAllow}
            total={total}
            colorClass="text-blue-400"
            barColorClass="bg-blue-500"
          />
          <GateRow
            label="Manual queued"
            count={breakdown.manualQueued}
            total={total}
            colorClass="text-amber-400"
            barColorClass="bg-amber-500"
          />
          <GateRow
            label="Rejected"
            count={breakdown.rejected}
            total={total}
            colorClass="text-red-400"
            barColorClass="bg-red-500"
          />
        </div>
      </CardContent>
    </Card>
  );
}
